<template>
  <RequiresPermission
    permission="view_users"
    fallback-route-name="merchantIndex"
  >
    <div>
      <div
        v-if="permissions.includes('manage_users')"
        class="mb-3 flex justify-end"
      >
        <RouterLink
          #default="{ href, navigate }"
          :to="{ name: 'userCreate' }"
          custom
        >
          <ActionButton
            as="a"
            :href="href"
            @click="navigate"
          >
            Create New User
          </ActionButton>
        </RouterLink>
      </div>
      <BaseCard class="relative">
        <QueryLoading v-if="isLoading" class="min-h-72" />
        <QueryError
          v-else-if="isUsersError"
          class="min-h-72"
          :show-retry="true"
          @retry="refetchUsers"
        >
          Failed to retrieve users
        </QueryError>
        <template v-else>
          <div class="mb-2 flex w-full justify-center">
            <div class="flex-1"> </div>
            <SearchInput v-model="rawSearch" />
            <div
              class="flex flex-1 items-center justify-end"
            >
            </div>
          </div>
          <BaseTable
            v-model:ordering="ordering"
            :rows="data?.items ?? []"
            :columns="columns"
            :is-loading="isFetching"
          >
            <template #cell-update="{ row }">
              <div
                v-if="
                  currentUser?.isSuperuser ||
                  (permissions.includes('manage_users') &&
                    row.email !== currentUser?.email)
                "
              >
                <BaseTableButton
                  :to="{
                    name: 'userEdit',
                    params: {
                      userId: row.id,
                    },
                  }"
                  variant="info"
                >
                  Edit
                </BaseTableButton>
              </div>
            </template>
            <template #cell-delete="{ row }">
              <BaseTableButton
                v-if="
                  permissions.includes('manage_users') &&
                  row.email !== currentUser?.email &&
                  !row.isSuperuser
                "
                variant="danger"
                @click="handleOpenDeleteUserModal(row.id)"
              >
                Delete
              </BaseTableButton>
            </template>
          </BaseTable>

          <div class="flex justify-end py-2">
            <BasePagination
              v-model="page"
              :page-count="pageCount"
            />
          </div>
        </template>
      </BaseCard>
    </div>
    <BaseConfirmation
      ref="deleteUserConfirmationRef"
      title="Delete User"
      description="Are you sure you want to delete this user?"
      confirm-label="Delete"
      cancel-label="Cancel"
    />
  </RequiresPermission>
</template>

<script setup lang="ts">
import { useRequiredPermissions } from "@/composables/useRequiredPermissions"
import type {
  TableColumn,
  TableOrdering,
} from "@/components/BaseTable.vue"
import type {
  ListUsersOrdering,
  UserFilter,
} from "@/services/users/listUsers"

const { permissions } = useAuth()
const { hasRequiredPermissions } =
  useRequiredPermissions("view_users")

const rawSearch = ref("")
const debouncedSearch = debouncedRef(rawSearch, 300)

const filters = computed<UserFilter>(() => ({
  search: debouncedSearch.value.trim(),
}))

const { page, pageCount, pagination, resetPagination } =
  usePagination({
    count: computed(() => data.value?.count || 0),
    limit: 10,
  })

const ordering = ref<
  TableOrdering<ListUsersOrdering> | undefined
>()
const deleteUserConfirmationRef = useTemplateRef(
  "deleteUserConfirmationRef"
)

const deleteIsSubmitting = ref(false)

const handleOpenDeleteUserModal = async (
  userId: string
) => {
  if (deleteUserConfirmationRef.value === null) return

  const { isCanceled } =
    await deleteUserConfirmationRef.value.reveal()

  if (isCanceled) return

  handleDeleteUser(userId)
}

const handleDeleteUser = async (userId: string) => {
  if (deleteIsSubmitting.value) return

  try {
    deleteIsSubmitting.value = true
    await deleteUser(userId)
    toast.success("User deleted")
  } catch {
    toast.error("Failed to delete user")
  } finally {
    deleteIsSubmitting.value = false
  }
}

watch(
  () => filters,
  () => resetPagination(),
  { deep: true }
)
const query = reactiveComputed(() => ({
  ...filters.value,
  ordering: ordering.value,
  ...pagination.value,
}))

const {
  data,
  isLoading,
  isFetching,
  isError: isUsersError,
  refetch: refetchUsers,
} = useUsers(query, hasRequiredPermissions)
const { currentUser } = useCurrentUser()

const now = useNow()

type ColumnId =
  | "email"
  | "name"
  | "role"
  | "lastLogin"
  | "createdAt"
  | "update"
  | "delete"

const columns: Array<
  TableColumn<
    ListUsersResponseItem,
    ColumnId,
    ListUsersOrdering
  >
> = [
  {
    id: "email",
    header: "Email",
    getCellValue: (row) => row.email,
    isSortable: true,
  },
  {
    id: "name",
    header: "Name",
    getCellValue: (row) =>
      `${row.firstName} ${row.lastName}`,
    isSortable: true,
  },
  {
    id: "role",
    header: "Role",
    getCellValue: (row) => row.role,
    isSortable: false,
  },
  {
    id: "lastLogin",
    header: "Last Login",
    getCellValue: (row) =>
      row.lastLogin
        ? DateFns.formatDistance(row.lastLogin, now.value, {
            addSuffix: true,
          })
        : "",
    isSortable: true,
  },
  {
    id: "createdAt",
    header: "Account Created",
    getCellValue: (row) =>
      row.createdAt
        ? DateFns.formatDistance(row.createdAt, now.value, {
            addSuffix: true,
          })
        : "",
    isSortable: true,
  },
  {
    id: "update",
    header: "",
    getCellValue: () => "",
    isSortable: false,
  },
  {
    id: "delete",
    header: "",
    getCellValue: () => "",
    isSortable: false,
  },
]
</script>
